import { Fragment, useState, type FormEvent } from 'react'
import { cleanVatRate, parsePriceCents, type ServiceDTO } from '@wf/shared'
import { VatSelect } from '../orders/VatSelect'
import { PreiseRow } from './PreiseRow'
import { useCreateService, useServicesList } from './useServices'

/**
 * Preise & Leistungen: der Festpreis-Katalog, aus dem Leistungen per Tipp auf einen Auftrag wandern (Port der
 * v6-Ansicht renderPreise). Anlegen oben, Suche, dann die nach Gruppe (Kategorie) gebündelte Preisliste mit
 * inline editierbaren Zeilen. Geld ausschließlich über @wf/shared. Lesen darf jeder Eingeloggte; das Anlegen/
 * Ändern erzwingt der Server über das Preise-Recht.
 */
export function PreisePage() {
  const { data, isLoading, error } = useServicesList()
  const create = useCreateService()
  const [query, setQuery] = useState('')
  const [name, setName] = useState('')
  const [purchasePrice, setPurchasePrice] = useState('')
  const [price, setPrice] = useState('')
  const [vatRate, setVatRate] = useState(19)
  const [category, setCategory] = useState('')

  function submitAdd(event: FormEvent) {
    event.preventDefault()
    if (!name.trim()) return
    create.mutate(
      {
        name: name.trim(),
        purchasePriceCents: parsePriceCents(purchasePrice),
        priceCents: parsePriceCents(price),
        vatRate: cleanVatRate(vatRate),
        category: category.trim() || null,
      },
      {
        onSuccess: () => {
          setName('')
          setPurchasePrice('')
          setPrice('')
          setVatRate(19)
          setCategory('')
        },
      },
    )
  }

  if (isLoading) return <div className="muted">Preisliste wird geladen…</div>
  if (error) {
    return (
      <div className="error" role="alert">
        Preisliste konnte nicht geladen werden.
      </div>
    )
  }

  const services = data?.services ?? []
  const q = query.trim().toLowerCase()
  const visible = q
    ? services.filter((service) => [service.name, service.category].some((field) => (field ?? '').toLowerCase().includes(q)))
    : services

  // Nach Gruppe (Kategorie || 'Allgemein') bündeln und alphabetisch ausgeben — wie der Alt-Monolith.
  const groups = new Map<string, ServiceDTO[]>()
  for (const service of visible) {
    const key = service.category?.trim() || 'Allgemein'
    const bucket = groups.get(key)
    if (bucket) bucket.push(service)
    else groups.set(key, [service])
  }
  const grouped = [...groups.entries()].sort((a, b) => a[0].localeCompare(b[0], 'de'))

  return (
    <section className="wf-view">
      <div className="wf-viewhead">
        <div>
          <h2 className="viewtitle">Leistungen</h2>
          <p className="sub">Deine Standard-Leistungen mit Festpreisen – mit einem Tipp in jeden Auftrag übernehmbar</p>
        </div>
      </div>

      <form className="wf-priceadd" onSubmit={submitAdd}>
        <input
          className="wf-input wf-priceadd__name"
          placeholder="Leistung (z. B. Reifenwechsel komplett)"
          aria-label="Leistung"
          value={name}
          onChange={(event) => setName(event.target.value)}
        />
        <input
          className="wf-input wf-tnum"
          placeholder="€ Einkauf (netto)"
          inputMode="decimal"
          aria-label="Einkaufspreis netto"
          value={purchasePrice}
          onChange={(event) => setPurchasePrice(event.target.value)}
        />
        <input
          className="wf-input wf-tnum"
          placeholder="€ Verkauf (netto)"
          inputMode="decimal"
          aria-label="Verkaufspreis netto"
          value={price}
          onChange={(event) => setPrice(event.target.value)}
        />
        <VatSelect value={vatRate} onChange={(r) => setVatRate(cleanVatRate(r))} label="MwSt" />
        <input
          className="wf-input"
          placeholder="Gruppe (optional, z. B. Reifen)"
          aria-label="Gruppe"
          value={category}
          onChange={(event) => setCategory(event.target.value)}
        />
        <button type="submit" className="wf-btn wf-btn--primary" disabled={create.isPending || !name.trim()}>
          ＋ Anlegen
        </button>
      </form>

      <input
        className="wf-input"
        type="search"
        placeholder="Leistung suchen"
        aria-label="Leistungen durchsuchen"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />

      <div className="wf-sectionhead">
        <h3>Preisliste</h3>
        <span className="wf-count">{visible.length}</span>
      </div>

      {visible.length === 0 ? (
        <div className="wf-empty">
          <div className="wf-empty__title">{services.length === 0 ? 'Noch keine Leistungen' : 'Keine Treffer'}</div>
          <div className="wf-empty__text">
            {services.length === 0 ? 'Leg oben die erste an – z. B. „Reifenwechsel komplett – 39,90 €".' : 'Andere Suche versuchen.'}
          </div>
        </div>
      ) : (
        grouped.map(([groupName, items]) => (
          <Fragment key={groupName}>
            <div className="wf-sectionhead">
              <h3>
                <span className="wf-chip wf-chip--static">{groupName}</span>
              </h3>
              <span className="wf-count">{items.length}</span>
            </div>
            <div className="wf-table">
              <div className="wf-table__head">
                <span className="wf-table__name">Bezeichnung</span>
                <span className="wf-table__price">EK netto</span>
                <span className="wf-table__price">VK netto</span>
                <span className="wf-table__vat">MwSt</span>
                <span className="wf-table__del" aria-hidden="true" />
              </div>
              {items.map((service) => (
                <PreiseRow key={service.id} service={service} />
              ))}
            </div>
          </Fragment>
        ))
      )}
    </section>
  )
}
